import { DecoratorNode, $applyNodeReplacement } from 'lexical';
import { markRaw } from 'vue';
import { guid } from '@/Utils';
import EditorMedia from '../Components/EditorMedia.vue';
import { blockRhythm, blockFrame, blockVideo, blockFlush, blockCaption } from '../Themes/EditorClasses';

function convertVideoElement(domNode) {
    const src = domNode.getAttribute('src');

    if (!src) return null;

    const width = domNode.getAttribute('data-video-width') || domNode.videoWidth || null;
    const height = domNode.getAttribute('data-video-height') || domNode.videoHeight || null;

    return { node: $createVideoNode({ src, width, height }) };
}

export class VideoNode extends DecoratorNode {
    __src;
    __width;
    __height;
    __caption;
    __spacing;

    static getType() {
        return 'video';
    }

    static clone(node) {
        return new VideoNode(node.__src, node.__width, node.__height, node.__caption, node.__spacing, node.__key);
    }

    constructor(src, width, height, caption, spacing, key) {
        super(key);
        this.__src = src;
        this.__width = width || null;
        this.__height = height || null;
        this.__caption = caption || '';
        this.__spacing = spacing !== false;
    }

    static importJSON(serializedNode) {
        const { src, width, height, caption, spacing } = serializedNode;
        return $createVideoNode({ src, width, height, caption, spacing });
    }

    exportJSON() {
        return {
            type: 'video',
            version: 1,
            src: this.__src,
            width: this.__width,
            height: this.__height,
            caption: this.__caption,
            spacing: this.__spacing,
        };
    }

    static importDOM() {
        return {
            video: () => ({
                conversion: convertVideoElement,
                priority: 0,
            }),
        };
    }

    exportDOM() {
        const figure = document.createElement('figure');
        figure.className = `${blockRhythm} ${blockFrame} ${blockVideo}`;
        if (!this.__spacing) {
            figure.classList.add(blockFlush);
        }

        // Mirrors the legacy Quill video embed markup exactly (nested spans, data-video-clip, no controls)
        // so stored content keeps working with the existing frontend/backend rendering logic.
        const outerWrapper = document.createElement('span');
        const innerWrapper = document.createElement('span');
        const clipWrapper = document.createElement('span');
        clipWrapper.setAttribute('data-video-clip', 'embed');

        const video = document.createElement('video');
        video.setAttribute('src', this.__src);
        video.setAttribute('muted', 'true');
        video.setAttribute('loop', 'true');
        video.setAttribute('playsinline', 'true');
        video.setAttribute('preload', 'metadata');
        video.setAttribute('id', `gfu-video-${guid()}`);

        if (this.__height) {
            video.setAttribute('data-video-height', String(this.__height));
        }

        if (this.__width) {
            video.setAttribute('data-video-width', String(this.__width));
        }

        clipWrapper.appendChild(video);
        innerWrapper.appendChild(clipWrapper);
        outerWrapper.appendChild(innerWrapper);
        figure.appendChild(outerWrapper);

        if (this.__caption) {
            const figcaption = document.createElement('figcaption');
            figcaption.className = blockCaption;
            figcaption.textContent = this.__caption;
            figure.appendChild(figcaption);
        }

        return { element: figure };
    }

    isInline() {
        return false;
    }

    createDOM() {
        return document.createElement('div');
    }

    updateDOM(prevNode) {
        return prevNode.__src !== this.__src
            || prevNode.__caption !== this.__caption
            || prevNode.__spacing !== this.__spacing;
    }

    setWidthAndHeight(width, height) {
        const writable = this.getWritable();
        writable.__width = width || null;
        writable.__height = height || null;
    }

    decorate(editor) {
        const aspectRatio = this.__width && this.__height
            ? this.__width / this.__height
            : null;

        return {
            component: markRaw(EditorMedia),
            props: {
                type: 'video',
                src: this.__src,
                caption: this.__caption,
                spacing: this.__spacing,
                aspectRatio,
                editable: editor.isEditable(),
            },
        };
    }

    getSrc() {
        return this.__src;
    }

    getCaption() {
        return this.__caption;
    }

    getSpacing() {
        return this.__spacing;
    }

    setSrc(src) {
        this.getWritable().__src = src;
    }

    setCaption(caption) {
        this.getWritable().__caption = caption || '';
    }

    setSpacing(spacing) {
        this.getWritable().__spacing = spacing !== false;
    }
}

export function $createVideoNode({ src, width, height, caption, spacing }) {
    return $applyNodeReplacement(new VideoNode(src, width, height, caption || '', spacing));
}

export function $isVideoNode(node) {
    return node instanceof VideoNode;
}
